/**
 * Dex live-scan pipeline telemetry — Push 10 / scan efficiency (2026-06-22).
 *
 * WHY THIS EXISTS. The live scanner already has rich per-gate `console.log`
 * markers (`[dex-scan] two-frame confirmed`, `present-write blocked (...)`,
 * etc.), but they are scattered across two modules and answer only "what did a
 * single gate decide?" They cannot answer the two questions a tester actually
 * asks when a scan feels slow or lossy:
 *
 *   1. LATENCY — how long, in frames, did each phase of the pipeline take?
 *      (page/context acquired → first useful numbers read → first row observed →
 *      first entry committed.) A scan that takes 40 frames to commit its first
 *      entry is "slow" in a way no single gate log reveals.
 *
 *   2. OBSERVE vs COMMIT — was a row's number actually READ in the right
 *      context (observe), even though strict write gates declined to APPLY the
 *      entry (commit)? Previously "seen" and "applied" were conflated: a G-Max
 *      row whose ids OCR'd cleanly but whose sprites failed the strict caught
 *      gate looked identical to a row that was never read at all. That makes a
 *      working scan indistinguishable from a broken one.
 *
 * THE ACQUIRE / OBSERVE / COMMIT SPLIT. This module is the OBSERVE-and-measure
 * layer. It is pure bookkeeping — recording a row as `observed` NEVER applies an
 * entry and NEVER relaxes a write gate. The strict commit gates in
 * `dexLiveFrameAdapter` + `dexLiveScanSession` remain the sole authority over
 * what mutates the dex. This recorder only watches what already happened and
 * timestamps it in frame-index space so the pipeline is debuggable.
 *
 * Pure + framework-agnostic so it is unit-tested deterministically.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * Why a frame produced no useful committed output, when that is the case. This
 * is the structured form of the existing scattered "held"/"blocked" reasons so
 * a tester can see, per frame, the single dominant reason nothing applied.
 * Frame-wide reasons (overlay text, no stable context, not a grid) are distinct
 * from candidate-local reasons (a single cell failed a gate) so the panel can
 * tell "the whole frame was junk" from "one cell was uncertain but the row was
 * still observed".
 */
export type FrameHeldReason =
  /** Frame was not a Pokémon GO dex grid (gate rejected it). */
  | "not_grid"
  /** Frame OCR confidence below the gate floor. */
  | "low_confidence"
  /** Nexus overlay / status HUD text contaminated the frame (frame-wide). */
  | "overlay_text"
  /** No variant could be resolved and no context was available (frame-wide). */
  | "no_context"
  /** Variant had not yet held for the 2-frame stability gate (frame-wide). */
  | "variant_unstable"
  /** Frame read no parsed dex numbers at all. */
  | "no_numbers"
  /** Rows WERE observed, but every write-eligible candidate is still pending
   *  corroboration (a normal, healthy hold — not an error). */
  | "awaiting_corroboration"
  /** Rows were observed, but every candidate was blocked by a per-cell write
   *  gate (membership / sprite / slot range). Candidate-local, not frame-wide. */
  | "candidates_blocked"
  /** The grid was still moving/settling (PoGo's own scroll jump), so the frame
   *  was read for coverage but not allowed to write. */
  | "scroll_settling"
  /** A candidate read caught on this frame but not-caught on earlier ones, so it
   *  waits for a second independent caught read before committing. */
  | "contested_catch";

/**
 * A single row's observation record for one session. A row is keyed by
 * `(variant, rowKey)` where `rowKey` is a stable per-frame row token (derived
 * from the cell yPct band — see `rowKeyForYPct`). `observedAtFrame` is the
 * 1-based frame index at which the row first had a reliable number read in the
 * correct context; `committedAtFrame` is when an entry from that row first
 * applied (may stay null forever if strict gates never pass — that is the whole
 * point of separating the two).
 */
export interface RowObservation {
  variant: PokedexVariant;
  rowKey: string;
  /** Distinct dex ids reliably read for this row across the session. */
  ids: number[];
  /** 1-based frame index of first reliable read in correct context. */
  observedAtFrame: number;
  /** 1-based frame index of first applied entry from this row, or null. */
  committedAtFrame: number | null;
}

/**
 * Frame-index latency markers for the whole session. Each is the 1-based frame
 * index at which the milestone first occurred, or null if it has not happened
 * yet. Measuring in FRAME INDEX (not wall-clock) keeps the markers deterministic
 * for tests and independent of device frame rate; the caller can pair them with
 * `lastFrameAt` wall-clock if a millisecond view is wanted.
 */
export interface ScanLatencyMarkers {
  /** Frame the context tracker first locked a page/region/variant. */
  contextLockedFrame: number | null;
  /** Frame OCR first read at least one usable dex number (any gate outcome). */
  firstNumbersFrame: number | null;
  /** Frame the first row was marked observed (reliable read, correct context). */
  firstRowObservedFrame: number | null;
  /** Frame the first entry was committed (applied to the store). */
  firstCommitFrame: number | null;
}

/**
 * Pre-context buffer lifecycle tallies (Push 11). Counts how the short-lived
 * top-row retention buffer behaved across the session — pure bookkeeping that
 * never affects a write. Lets the next device trace answer "did the G-Max row-1
 * retention actually fire, and did it promote or just expire?".
 */
export interface PreContextMarkers {
  /** Frames that retained at least one row into the pre-context buffer. */
  observed: number;
  /** Lock events that promoted buffered rows back for re-observation. */
  promoted: number;
  /** Distinct buffered rows that aged out past the TTL before promotion. */
  expired: number;
  /** Times a variant switch cleared the buffer (rows under the old variant). */
  variantSwitchReset: number;
  /** Distinct dex ids handed back across all promotions this session. */
  promotedIds: number;
}

/**
 * Mid-session dex-switch tallies (telemetry 2026-06-22). Counts how the
 * scan-context tracker's text-driven switch path behaved across the session —
 * pure bookkeeping that never affects a write. Lets the next device trace answer
 * "did the Perfect→G-Max→Purified switch actually fire, and did it lock or get
 * rejected?". Mirrors the tracker's `switch_requested` / `switch_locked` /
 * `switch_rejected` events one-to-one.
 */
export interface MidSessionSwitchMarkers {
  /** Frames a strong-text switch vote was accruing (not yet decisive). */
  requested: number;
  /** Times a sustained switch vote evicted the old lock and re-acquired. */
  locked: number;
  /** Times an in-flight switch vote was abandoned (original variant reaffirmed). */
  rejected: number;
}

export interface ScanTelemetrySnapshot {
  latency: ScanLatencyMarkers;
  /** Distinct rows observed this session (reliable read, may not have applied). */
  rowsObserved: number;
  /** Subset of rowsObserved that have committed at least one entry. */
  rowsCommitted: number;
  /** Distinct rows observed whose entries never applied (observed-only). */
  rowsObservedNotCommitted: number;
  /** Per-reason tally of frames that produced no committed output. */
  heldFrames: Record<FrameHeldReason, number>;
  /** Pre-context buffer lifecycle tallies (Push 11). */
  preContext: PreContextMarkers;
  /** Mid-session dex-switch tallies (telemetry 2026-06-22). */
  midSessionSwitch: MidSessionSwitchMarkers;
  /** All row observations, for detailed inspection / tests. */
  rows: RowObservation[];
}

/**
 * Stable per-frame ROW token from a cell's yPct. The adapter lays rows out at
 * `GRID_START_Y + (rowIndex + 0.5) * CELL_HEIGHT` (~0.16 per row), so quantising
 * yPct to the nearest 0.16 band recovers a row identity without needing the grid
 * stride here (G-Max uses a different column count, but its ROWS still occupy the
 * same vertical bands). Observations with no yPct (older adapters / tests) fall
 * into a single "r?" bucket so they still count as one row.
 */
export function rowKeyForYPct(yPct: number | undefined): string {
  if (yPct == null || !Number.isFinite(yPct)) return "r?";
  // Row centres sit at 0.17 + (rowIndex + 0.5) * 0.16 → 0.25, 0.41, 0.57, 0.73.
  // Anchor on the FIRST centre (0.25) and step by the 0.16 row height so each
  // centre rounds back to its own band: (0.25-0.25)/0.16=0, (0.41-0.25)/0.16=1…
  const band = Math.round((yPct - 0.25) / 0.16);
  return `r${band}`;
}

function emptyHeldFrames(): Record<FrameHeldReason, number> {
  return {
    not_grid: 0,
    low_confidence: 0,
    overlay_text: 0,
    no_context: 0,
    variant_unstable: 0,
    no_numbers: 0,
    awaiting_corroboration: 0,
    candidates_blocked: 0,
    scroll_settling: 0,
    contested_catch: 0,
  };
}

const TELEMETRY_LOG_PREFIX = "[dex-scan] telemetry";

/**
 * Create a stateful scan-telemetry recorder for one session. Every method is
 * pure bookkeeping; none of them ever decides whether an entry applies.
 *
 * `log` defaults to `console.log` so the structured markers also appear in the
 * on-device Xcode trace alongside the existing gate logs; pass a no-op (or a
 * capturing array) in tests to keep output clean.
 */
export function createScanTelemetry(opts?: {
  log?: (event: string, fields: Record<string, unknown>) => void;
}) {
  const log =
    opts?.log ??
    ((event: string, fields: Record<string, unknown>) => {
      // eslint-disable-next-line no-console
      console.log(`${TELEMETRY_LOG_PREFIX} ${event}`, fields);
    });

  const latency: ScanLatencyMarkers = {
    contextLockedFrame: null,
    firstNumbersFrame: null,
    firstRowObservedFrame: null,
    firstCommitFrame: null,
  };
  const heldFrames = emptyHeldFrames();
  const preContext: PreContextMarkers = {
    observed: 0,
    promoted: 0,
    expired: 0,
    variantSwitchReset: 0,
    promotedIds: 0,
  };
  const midSessionSwitch: MidSessionSwitchMarkers = {
    requested: 0,
    locked: 0,
    rejected: 0,
  };
  // Keyed "variant rowKey" so two variants' same yPct band are distinct rows.
  const rows = new Map<string, RowObservation>();

  function rowMapKey(variant: PokedexVariant, rowKey: string): string {
    return `${variant} ${rowKey}`;
  }

  // Human, grep-friendly event name per latency marker so a TestFlight/Xcode
  // trace can be searched for the phase directly (e.g. "first commit") rather
  // than the camelCase field name. The marker field stays in the payload for
  // machine parsing; the event string is what a tester eyeballs/greps.
  const MARKER_EVENT: Record<keyof ScanLatencyMarkers, string> = {
    contextLockedFrame: "context locked",
    firstNumbersFrame: "first numbers read",
    firstRowObservedFrame: "first row observed",
    firstCommitFrame: "first commit",
  };

  function markFirst(field: keyof ScanLatencyMarkers, frameIndex: number) {
    if (latency[field] == null) {
      latency[field] = frameIndex;
      log(MARKER_EVENT[field], { marker: field, frame: frameIndex });
    }
  }

  return {
    /**
     * The context tracker locked a page identity this frame. Records the
     * acquire-phase latency marker. Idempotent (only the first lock is timed).
     */
    noteContextLocked(frameIndex: number): void {
      markFirst("contextLockedFrame", frameIndex);
    },

    /**
     * OCR read at least one usable dex number this frame, regardless of whether
     * any gate later let it through. This is the "first useful numbers read"
     * marker the brief asks for — it fires BEFORE any commit gate.
     */
    noteNumbersRead(frameIndex: number, count: number): void {
      if (count <= 0) return;
      markFirst("firstNumbersFrame", frameIndex);
    },

    /**
     * Mark a ROW as observed/seen: reliable numbers for the row were read in the
     * correct context this frame. This is INDEPENDENT of apply — a row counts as
     * observed even if every one of its entries is later blocked by a strict
     * write gate. `ids` are the reliable dex ids read for the row this frame.
     *
     * Returns true when this was the row's FIRST observation (useful for tests).
     */
    observeRow(args: {
      variant: PokedexVariant;
      rowKey: string;
      ids: number[];
      frameIndex: number;
    }): boolean {
      const { variant, rowKey, ids, frameIndex } = args;
      const key = rowMapKey(variant, rowKey);
      let row = rows.get(key);
      const isFirst = row == null;
      if (!row) {
        row = {
          variant,
          rowKey,
          ids: [],
          observedAtFrame: frameIndex,
          committedAtFrame: null,
        };
        rows.set(key, row);
        markFirst("firstRowObservedFrame", frameIndex);
        log("row observed", { variant, row: rowKey, ids, frame: frameIndex });
      }
      for (const id of ids) {
        if (!row.ids.includes(id)) row.ids.push(id);
      }
      return isFirst;
    },

    /**
     * Record that an entry from a row COMMITTED (applied to the store) this
     * frame. Stamps the row's `committedAtFrame` (first commit only) and the
     * session's `firstCommitFrame`. Recording a commit for a row that was never
     * explicitly observed back-fills the observation so the counters stay
     * consistent (a committed row is, by definition, an observed one).
     */
    commitRow(args: {
      variant: PokedexVariant;
      rowKey: string;
      ids: number[];
      frameIndex: number;
    }): void {
      const { variant, rowKey, ids, frameIndex } = args;
      const key = rowMapKey(variant, rowKey);
      let row = rows.get(key);
      if (!row) {
        row = {
          variant,
          rowKey,
          ids: [...ids],
          observedAtFrame: frameIndex,
          committedAtFrame: null,
        };
        rows.set(key, row);
        markFirst("firstRowObservedFrame", frameIndex);
      }
      for (const id of ids) {
        if (!row.ids.includes(id)) row.ids.push(id);
      }
      if (row.committedAtFrame == null) {
        row.committedAtFrame = frameIndex;
        log("row committed", {
          variant,
          row: rowKey,
          ids,
          frame: frameIndex,
          observeToCommitFrames: frameIndex - row.observedAtFrame,
        });
      }
      markFirst("firstCommitFrame", frameIndex);
    },

    /**
     * Record the dominant reason a frame produced no committed output. Pure
     * tally; never affects a write. `held` is intentionally permissive about
     * frame-wide vs candidate-local — the caller chooses the most specific
     * reason it can (see `dexLiveScanSession`).
     */
    noteHeld(reason: FrameHeldReason): void {
      heldFrames[reason] += 1;
    },

    /**
     * Pre-context buffer lifecycle markers (Push 11). Pure tallies that mirror
     * the [[createPreContextBuffer]] events so a device trace shows whether the
     * top-row retention buffer retained, promoted, expired, or reset on a switch.
     * None of these ever applies an entry or relaxes a gate.
     */
    notePreContextObserved(args: { buffered: number; frameIndex: number }): void {
      preContext.observed += 1;
      log("pre-context observed", { buffered: args.buffered, frame: args.frameIndex });
    },
    notePreContextPromoted(args: {
      variant: PokedexVariant;
      ids: number[];
      frameIndex: number;
    }): void {
      preContext.promoted += 1;
      preContext.promotedIds += args.ids.length;
      log("pre-context promoted", {
        variant: args.variant,
        ids: [...args.ids].sort((a, b) => a - b),
        frame: args.frameIndex,
      });
    },
    notePreContextExpired(args: { count: number; frameIndex: number }): void {
      if (args.count <= 0) return;
      preContext.expired += args.count;
      log("pre-context expired", { count: args.count, frame: args.frameIndex });
    },
    notePreContextVariantSwitchReset(args: { frameIndex: number }): void {
      preContext.variantSwitchReset += 1;
      log("pre-context variant switch reset", { frame: args.frameIndex });
    },

    /**
     * Mid-session dex-switch markers (telemetry 2026-06-22). Pure tallies that
     * mirror the [[createScanContextTracker]] `switch_requested` / `switch_locked`
     * / `switch_rejected` events so a device trace shows whether a tab change
     * (Perfect→G-Max→Purified) was detected, locked, or rejected as noise. None of
     * these ever applies an entry or relaxes a gate — the switch only re-routes the
     * provisional context the unchanged write gates then re-judge.
     */
    noteContextSwitchRequested(args: {
      target: PokedexVariant | null;
      votes: number;
      frameIndex: number;
      /** The variant the lock held when the vote was raised ("from" side). */
      from?: PokedexVariant | null;
      /**
       * Why the switch was raised — `strong_chip_text_on_hold` when the frame's
       * routed variant was vetoed/held (the focused #476 case where strong
       * PURIFIED text existed on a variant_hold frame), `strong_chip_text` for an
       * ordinary disagreement on a frame that still routed a read.
       */
      reason?: "strong_chip_text_on_hold" | "strong_chip_text";
    }): void {
      midSessionSwitch.requested += 1;
      log("scan context switch requested", {
        from: args.from ?? null,
        target: args.target,
        votes: args.votes,
        reason: args.reason ?? null,
        frame: args.frameIndex,
      });
    },
    noteContextSwitchLocked(args: {
      target: PokedexVariant | null;
      votes: number;
      frameIndex: number;
      /** The variant the lock held when the switch tripped ("from" side). */
      from?: PokedexVariant | null;
    }): void {
      midSessionSwitch.locked += 1;
      log("scan context switch locked", {
        from: args.from ?? null,
        target: args.target,
        votes: args.votes,
        frame: args.frameIndex,
      });
    },
    noteContextSwitchRejected(args: { frameIndex: number }): void {
      midSessionSwitch.rejected += 1;
      log("scan context switch rejected", { frame: args.frameIndex });
    },

    snapshot(): ScanTelemetrySnapshot {
      const allRows = Array.from(rows.values()).map((r) => ({
        ...r,
        ids: [...r.ids].sort((a, b) => a - b),
      }));
      const rowsCommitted = allRows.filter((r) => r.committedAtFrame != null).length;
      return {
        latency: { ...latency },
        rowsObserved: allRows.length,
        rowsCommitted,
        rowsObservedNotCommitted: allRows.length - rowsCommitted,
        heldFrames: { ...heldFrames },
        preContext: { ...preContext },
        midSessionSwitch: { ...midSessionSwitch },
        rows: allRows,
      };
    },

    /**
     * Emit the whole-session summary to the structured log in three concise,
     * grep-friendly lines so a tester can answer "where did the scan spend its
     * frames?" from an Xcode/TestFlight trace WITHOUT a debug build:
     *
     *   [dex-scan] telemetry snapshot   — latency markers + observe/commit split
     *   [dex-scan] telemetry observe-vs-commit — rows observed vs committed
     *   [dex-scan] telemetry held reasons — only the non-zero held tallies
     *
     * Call once at session end (and/or on demand). Pure read of the recorder;
     * it never mutates state, so calling it twice is safe and idempotent.
     */
    logSnapshot(): void {
      const snap = this.snapshot();
      log("snapshot", {
        contextLockedFrame: snap.latency.contextLockedFrame,
        firstNumbersFrame: snap.latency.firstNumbersFrame,
        firstRowObservedFrame: snap.latency.firstRowObservedFrame,
        firstCommitFrame: snap.latency.firstCommitFrame,
        rowsObserved: snap.rowsObserved,
        rowsCommitted: snap.rowsCommitted,
        rowsObservedNotCommitted: snap.rowsObservedNotCommitted,
      });
      log("observe-vs-commit", {
        rowsObserved: snap.rowsObserved,
        rowsCommitted: snap.rowsCommitted,
        rowsObservedNotCommitted: snap.rowsObservedNotCommitted,
        rows: snap.rows.map((r) => ({
          variant: r.variant,
          row: r.rowKey,
          ids: r.ids,
          observedAtFrame: r.observedAtFrame,
          committedAtFrame: r.committedAtFrame,
        })),
      });
      // Only surface reasons that actually fired so the line stays readable.
      const heldNonZero = Object.fromEntries(
        (Object.entries(snap.heldFrames) as [FrameHeldReason, number][]).filter(
          ([, n]) => n > 0,
        ),
      );
      log("held reasons", heldNonZero);
      // Pre-context buffer summary (Push 11): one line so a tester can see at a
      // glance whether top-row retention fired and what it did with the rows.
      log("pre-context", { ...snap.preContext });
      // Mid-session switch summary (telemetry 2026-06-22): one line so a tester
      // can see whether a tab change fired and whether it locked or was rejected.
      log("mid-session switch", { ...snap.midSessionSwitch });
    },

    /** Reset between scan sessions. */
    reset(): void {
      latency.contextLockedFrame = null;
      latency.firstNumbersFrame = null;
      latency.firstRowObservedFrame = null;
      latency.firstCommitFrame = null;
      for (const k of Object.keys(heldFrames) as FrameHeldReason[]) {
        heldFrames[k] = 0;
      }
      preContext.observed = 0;
      preContext.promoted = 0;
      preContext.expired = 0;
      preContext.variantSwitchReset = 0;
      preContext.promotedIds = 0;
      midSessionSwitch.requested = 0;
      midSessionSwitch.locked = 0;
      midSessionSwitch.rejected = 0;
      rows.clear();
    },
  };
}

export type ScanTelemetry = ReturnType<typeof createScanTelemetry>;
